import { ComponentProps, ReactElement, cloneElement } from 'react';

import { Button } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { WorkflowInstance } from '../../hooks/useGetWorkflowInstance';
import { useWorkflowCreditAvailability } from '../../hooks/useWorkflowCreditAvailability';

type WorkflowCreditGuardProps = {
  children: ReactElement<ComponentProps<typeof Button>>;
  workflowInstance: Pick<WorkflowInstance, 'pendingTransition'>;
  retry?: boolean;
  tooltip?: string;
};

export const WorkflowCreditGuard = ({
  children,
  workflowInstance,
  retry = false,
  tooltip,
}: WorkflowCreditGuardProps): ReactElement => {
  const disabled =
    children.props.disabled ||
    children.props.isVisuallyDisabled ||
    children.props['aria-disabled'] === true ||
    children.props['aria-disabled'] === 'true';
  const credit = useWorkflowCreditAvailability({
    skip: !!disabled || (retry && !!workflowInstance.pendingTransition),
  });
  const blocked = !!disabled || credit.blocked;
  const reason = credit.blocked
    ? 'Not enough credits to perform this action.'
    : tooltip;

  return (
    <TooltipRoot>
      {cloneElement(children, {
        isVisuallyDisabled: blocked,
        'aria-disabled': blocked,
        onClick: blocked
          ? undefined
          : event => {
              void credit.refresh();
              children.props.onClick?.(event);
            },
      })}
      {reason ? <Tooltip>{reason}</Tooltip> : null}
    </TooltipRoot>
  );
};
